import React from 'react';
import type { NextPage } from 'next';
import { getClientIp } from '@surfline/web-common';

import { setSpotLoading } from 'actions/spot';
import {
  fetchFavoriteSubregions,
  fetchForecasterContent,
  fetchNearbySubregions,
  fetchOverviewData,
  setSubregionLoading,
} from 'actions/subregion';
import { getUser } from 'common/api/user';
import SubregionForecast from 'containers/SubregionForecast';
import type { AppPageProps } from 'pages/_app';
import { getOrCreateStore } from 'stores';
import { PermissionTypes } from 'types/permissions';
import type { WavetrakContext, WavetrakPageContext } from 'types/wavetrak';
import getCloudflareCountryCode from 'utils/getCloudflareCountryCode';
import { forecastDaysAllowed } from 'utils/forecastDaysAllowed';

interface PageProps extends AppPageProps {}

const SubregionForecastPage: NextPage<PageProps> = () => <SubregionForecast />;

SubregionForecastPage.getInitialProps = async (
  context: WavetrakPageContext,
): Promise<PageProps> => {
  const store = getOrCreateStore();
  const { dispatch } = store;
  const { query, req } = context;

  const { subregionSlug, subregionId } = query as {
    subregionSlug: string;
    subregionId: string;
  };

  const cookies = req?.cookies || {};

  const clientIp = getClientIp(context.req);

  const isServer = !!req;

  const countryCode = getCloudflareCountryCode(req);

  const userData = await getUser(context as WavetrakContext, isServer);

  const has16DayPermission = userData?.permissions?.some(
    (permission) => permission.name === PermissionTypes.CORE_16DAY_FORECAST,
  );

  const daysAllowed = forecastDaysAllowed({
    type: 'forecast-content',
    isEntitled: has16DayPermission,
    query,
  });

  await dispatch(setSpotLoading(true));
  await dispatch(setSubregionLoading(true));
  await Promise.all([
    dispatch(fetchOverviewData(subregionId, cookies, clientIp, true, countryCode)),
    dispatch(fetchNearbySubregions(subregionId, cookies)),
    dispatch(fetchFavoriteSubregions(cookies)),
    dispatch(
      fetchForecasterContent({
        cookies,
        days: daysAllowed,
        subregionId,
      }),
    ),
  ]);
  await dispatch(setSubregionLoading(false));
  await dispatch(setSpotLoading(false));

  if (isServer) {
    const newrelic = require('newrelic');
    newrelic.addCustomAttributes({
      subregionSlug,
      subregionId,
    });
    return {
      enableCssBaseline: true,
      enableTheme: true,
      isSubregionForecastPage: true,
      librariesToLoad: {
        jwplayer: true,
      },
      ssrReduxState: store.getState(),
    };
  }

  return {
    enableCssBaseline: true,
    enableTheme: true,
    isSubregionForecastPage: true,
    librariesToLoad: {
      jwplayer: true,
    },
  };
};

export default SubregionForecastPage;
